import { useState, useCallback, useRef } from "react";
import { api } from "../../../../lib/api";

/**
 * useAIImport — single-shot hook that uploads a poster to the AI import
 * endpoint and returns the extraction result.
 *
 * Design:
 *   - No global state, no context — the modal owns the lifecycle.
 *   - `run(file)` uploads + waits for the response. It resolves with the
 *     full backend payload or throws with a user-friendly Italian message.
 *   - `cancel()` aborts an in-flight request (AbortController).
 */
export function useAIImport() {
  const [state, setState] = useState({
    status: "idle",       // idle | uploading | analyzing | success | error
    result: null,
    error: null,
    stepIndex: 0,
  });
  const controllerRef = useRef(null);
  const stepTimerRef = useRef(null);

  const stepMessages = [
    "Sto leggendo la locandina…",
    "Sto riconoscendo data, luogo e orari…",
    "Sto preparando la descrizione dell'evento…",
    "Sto completando la scheda…",
  ];

  const stopStepper = () => {
    if (stepTimerRef.current) {
      clearInterval(stepTimerRef.current);
      stepTimerRef.current = null;
    }
  };

  const startStepper = () => {
    stopStepper();
    let i = 0;
    setState((s) => ({ ...s, stepIndex: 0 }));
    stepTimerRef.current = setInterval(() => {
      i = Math.min(i + 1, stepMessages.length - 1);
      setState((s) => ({ ...s, stepIndex: i }));
    }, 3500);
  };

  const cancel = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    stopStepper();
    setState({ status: "idle", result: null, error: null, stepIndex: 0 });
  }, []);

  const run = useCallback(async (file) => {
    if (!file) return null;
    const controller = new AbortController();
    controllerRef.current = controller;
    setState({ status: "analyzing", result: null, error: null, stepIndex: 0 });
    startStepper();
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/events-v2/import-poster", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: controller.signal,
        timeout: 60000,
      });
      stopStepper();
      setState({ status: "success", result: res.data, error: null, stepIndex: stepMessages.length - 1 });
      return res.data;
    } catch (err) {
      stopStepper();
      if (err?.name === "CanceledError" || err?.message === "canceled") {
        setState({ status: "idle", result: null, error: null, stepIndex: 0 });
        return null;
      }
      const detail = err?.response?.data?.detail || err?.message || "Errore durante l'analisi";
      setState({ status: "error", result: null, error: detail, stepIndex: 0 });
      throw new Error(detail);
    } finally {
      controllerRef.current = null;
    }
  }, []);

  return { ...state, run, cancel, stepMessage: stepMessages[state.stepIndex] };
}
